import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from './alert.service';

/**
 * User-initiated logout with a shared "are you sure?" confirmation, so every
 * eMeni app shows the same prompt from one place (waiter, kitchen, orders,
 * kiosk, backoffice). Adopt it by injecting the service and calling
 * `confirmAndSignOut()` from the logout button.
 *
 * IMPORTANT — only for logout the USER deliberately chose. Forced/automatic
 * logouts must NOT go through here, or they would pop a pointless prompt (and
 * let the user "cancel" a session the backend already killed):
 *   - session expired / invalid token  → AuthorizationService.signOut() and the
 *     accessDeniedInterceptor route to /logout directly,
 *   - inactivity timer                  → MainComponent.inactivityElapsed().
 * Those stay silent. Keep them that way.
 */
@Injectable({ providedIn: 'root' })
export class LogoutService {
  constructor(
    private alert: AlertService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  /**
   * Show the shared logout confirmation. Resolves true when the user confirms.
   * `text` overrides the body with an ALREADY-TRANSLATED string for app-specific
   * wording — e.g. the waiter PIN screen warns that PIN login will be disabled
   * (that wording and its translations live in the waiter app, not core). Pass a
   * resolved string, not a translation key. Omit it for the generic core default.
   */
  confirmLogout(text?: string): Promise<boolean> {
    return this.alert.confirm({
      type: 'question',
      title: this.translate.instant('logoutConfirmationTitle'),
      text: text ?? this.translate.instant('logoutConfirmationText'),
      confirmButtonText: this.translate.instant('confirmYes'),
      cancelButtonText: this.translate.instant('confirmNo'),
    }).then((result) => result.confirmed);
  }

  /** Confirm, then fully sign out via the /logout route. No-op if cancelled. */
  async confirmAndSignOut(text?: string): Promise<void> {
    if (await this.confirmLogout(text)) {
      this.router.navigateByUrl('/logout');
    }
  }
}
